import { useCallback, useEffect, useRef, useState } from "react";

import { resolveInvite } from "src/api/client";
import type { InviteResolution } from "src/api/types";

const POLL_INTERVAL = 60_000;

interface UseInviteResult {
  data: InviteResolution | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useInvite(shortCode: string, enabled: boolean = true): UseInviteResult {
  const [data, setData] = useState<InviteResolution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const fetchInvite = useCallback(async () => {
    try {
      const result = await resolveInvite(shortCode);
      setData(result);
      setError(null);
    } catch (err) {
      // a later poll failing after an earlier success shouldn't discard a working screen
      if (dataRef.current === null) setError((err as Error).message);
    }
    setLoading(false);
  }, [shortCode]);

  useEffect(() => {
    if (!enabled) return;
    // Initial load; state is only set after the request resolves.
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    void fetchInvite();
    const id = setInterval(() => void fetchInvite(), POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchInvite, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handler = () => void fetchInvite();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [fetchInvite, enabled]);

  return { data, loading, error, refetch: fetchInvite };
}
